/**
 * impact.utils.js - INTEGRATED LCA MODEL
 * Logic is live, but Farm and Logistics are zeroed out until verified data is received.
 */
import { SUSTAINABILITY_DEV_MODE as DEV_MODE } from './url.utils';

export const CARBON_DATA = {
  // 1. FARM LAYER
  FARM: {
    EUROPE: { 
      GOOSE: 0.00000, // Placeholder was 0.00250
      DUCK: 0.00000   // Placeholder was 0.00180
    },
    CHINA: { 
      GOOSE: 0.00000, 
      DUCK: 0.00000 
    },
    RECYCLED: 0.00000 
  },

  // 2. TRANSPORTATION LAYER
  LOGISTICS: {
    EUROPE_TO_US: 0.00000, // Placeholder was 0.00045
    INTRA_CHINA: 0.00000,
    DEFAULT: 0.00000
  },

  // 3. PRODUCTION LAYER (KEPT LIVE - Verified by Sheet)
  CN: { PRODUCTION_FLAT: 0.00030 },
  US: {
    ANCHORS: { RENU: 0.00129, VIRGIN_PREMIUM: 0.00129, VIRGIN_BASE: 0.00030 },
    FP_STEP_DELTA: 0.00005 
  },

  // 4. TREATMENT LAYER (VERIFIED VALUES PENDING)
  TREATMENTS: { 
    HYPERDRY: 0.00000,
    EXPEDRY: 0.00000,
    EXPEDRY_PLUS: 0.00000,
    ULTRAWHT: 0.00000
  },

  // 5. ABATTOIR LAYER (New)
  ABATTOIR: 0.00011, // Verified baseline

  // 6. SYNTHETIC BASELINE (Pass-through for comparison logic)
  // SYNTHETIC_BASELINE: 0.00415 (old value)
  SYNTHETIC_BASELINE: 0.00848
};

export const calculateImpact = (data) => {
  if (!data) return { total: 0, farm: 0, transportation: 0, production: 0, treatment: 0 };

  // --- 0. PREFER VERIFIED DATABASE IMPACT ---
  if (data.impact) {
    const dbTotal = parseFloat(data.impact.total_impact || data.impact.total || 0);
    return {
      total: dbTotal,
      farm: parseFloat(data.impact.farm || 0),
      transportation: parseFloat(data.impact.transportation || 0),
      production: parseFloat(data.impact.production || 0),
      treatment: parseFloat(data.impact.treatment || 0),
      abattoir: parseFloat(data.impact.abattoir || 0) // Include abattoir
    };
  }

  // --- DATA NORMALIZATION ---
  const originArray = Array.isArray(data.origin) ? data.origin : [data.origin];
  const isEurope = originArray.includes("Europe");
  const isChina = originArray.includes("China");
  const isUS = data.facility?.toUpperCase().includes("US") || 
               data.facility?.toUpperCase().includes("LOS ANGELES") || 
               isEurope;

  const type = (data.down_type || "").toLowerCase();
  const fp = parseInt(data.verified_fp) || 600;
  const isRenu = type.includes("renu") || data.recycled === true;
  
  const treatmentArray = Array.isArray(data.treatment) ? data.treatment : [data.treatment];

  let farmScore = 0;
  let transpoScore = 0;
  let productionScore = 0;
  let treatmentScore = 0;
  let abattoirScore = 0; // Initialize abattoirScore

  // --- ABATTOIR ---
  if (isChina) {
    abattoirScore = CARBON_DATA.ABATTOIR; // Calculate abattoirScore
  }
  // --- 1. FARM ---
  if (isRenu) {
    farmScore = CARBON_DATA.FARM.RECYCLED;
  } else {
    const speciesKey = type.includes("goose") ? "GOOSE" : "DUCK";
    const originKey = isEurope ? "EUROPE" : "CHINA";
    farmScore = CARBON_DATA.FARM[originKey][speciesKey];
  }

  // --- 2. TRANSPORTATION ---
  if (isEurope && isUS) {
    transpoScore = CARBON_DATA.LOGISTICS.EUROPE_TO_US;
  } else if (!isEurope && !isUS) {
    transpoScore = CARBON_DATA.LOGISTICS.INTRA_CHINA;
  } else {
    transpoScore = CARBON_DATA.LOGISTICS.DEFAULT;
  }

  // --- 3. PRODUCTION ---
  if (!isUS) {
    productionScore = CARBON_DATA.CN.PRODUCTION_FLAT;
  } else {
    if (isRenu) productionScore = CARBON_DATA.US.ANCHORS.RENU;
    else if (type.includes("grey") && type.includes("duck") && !type.includes("goose")) {
      productionScore = CARBON_DATA.US.ANCHORS.VIRGIN_BASE;
    } else productionScore = CARBON_DATA.US.ANCHORS.VIRGIN_PREMIUM;

    if (fp > 600) {
      const steps = Math.floor((fp - 600) / 50);
      productionScore += (steps * CARBON_DATA.US.FP_STEP_DELTA);
    }
  }

  // --- 4. TREATMENT ---
  treatmentArray.forEach(t => {
    if (!t) return;
    const cleanT = t.toUpperCase().replace(/\+/g, '_PLUS').replace(/\s+/g, '');
    if (CARBON_DATA.TREATMENTS[cleanT] !== undefined) {
      treatmentScore += CARBON_DATA.TREATMENTS[cleanT];
    }
  });

  const finalTotal = Number((farmScore + transpoScore + productionScore + treatmentScore + abattoirScore).toFixed(5));

  return {
    total: finalTotal,
    farm: farmScore,
    transportation: transpoScore,
    production: productionScore,
    treatment: treatmentScore,
    abattoir: abattoirScore // Return abattoirScore
  };
};